import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom, Observable } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { MMU_LOG_LEVEL } from '../../logging';
import { MmuDefaultLogger } from '../../logging/mmu-logger';
import { MmuRemoteModuleLoadConfig } from '../interfaces/MmuRemoteModuleLoadConfig';


type Scope = unknown;
type RemoteModule = any;
type Container = {
  init(shareScope: Scope): void;
  get(module: string): RemoteModule;
};

// These are global constants defined by webpack
// eslint-disable-next-line @typescript-eslint/naming-convention
declare const __webpack_init_sharing__: (shareScope: string) => Promise<void>;
// eslint-disable-next-line @typescript-eslint/naming-convention
declare const __webpack_share_scopes__: { default: Scope };


export class RemoteModuleLoadingError<T = unknown> extends Error {
  constructor(msg: string, readonly reason: T) {
    super(msg);
    // Set the prototype explicitly.
    Object.setPrototypeOf(this, RemoteModuleLoadingError.prototype);
  }
}

@Injectable({
  providedIn: 'root'
})
export class MmuRemoteModuleService {
  private static _instance: MmuRemoteModuleService;

  protected _isDefaultScopedInitialized = false;

  private _http = inject(HttpClient);
  private _logger = new MmuDefaultLogger('MmuRemoteModuleService', inject(MMU_LOG_LEVEL));
  private _manifestRequestCacheMap = new Map<string, Observable<any>>();
  private _scriptLoadCacheMap = new Map<string, Promise<void>>();
  private _containerRegistry = new Map<string, Container>();
  private _moduleRegistry = new Map<string, Promise<RemoteModule>>();

  static get instance(): MmuRemoteModuleService {
    return MmuRemoteModuleService._instance;
  }

  get isInitialized(): boolean {
    return this._isDefaultScopedInitialized;
  }

  constructor() {
    if (MmuRemoteModuleService.instance) {
      throw new Error('MmuRemoteModuleService has already been created. This class should be a singleton!');
    }
    MmuRemoteModuleService._instance = this;
    this.initWebpackShareScope().then();
  }

  /**
   * Load and return a remote module instance. Automatically adds the required script to the DOM, initializes the webpack sharing and
   * creates a module instance which is returned. Static method is required for use in Angular Routing.
   *
   * @param config Info where to find the module (module name, manifest url and file name)
   */
  static async loadRemoteModule(config: MmuRemoteModuleLoadConfig): Promise<any> {
    return this.instance.loadRemoteModule(config);
  }

  /**
   * Load and return a remote module instance. Automatically adds the required script to the DOM, initializes the webpack sharing and
   * creates a module instance which is returned.
   *
   * @param config Info where to find the module (module name, manifest url and file name)
   */
  async loadRemoteModule(config: MmuRemoteModuleLoadConfig): Promise<RemoteModule> {
    const { manifestUrl, moduleFile, remoteModule } = config;

    if (!config || !config.moduleFile || !config.remoteModule) {
      return Promise.reject('MmuRemoteModuleService: Load config incomplete. Please make sure all properties are defined.');
    }

    if (this._moduleRegistry.has(remoteModule)) {
      return this._moduleRegistry.get(remoteModule);
    }

    /* eslint-disable-next-line no-async-promise-executor  */
    const promise = new Promise<RemoteModule>(async (resolve, reject) => {
      this._logger.debug(`Loading remote module "${remoteModule}"...`);

      try {
        // Try to resolve the real, hashed URL by using its manifest
        const url = manifestUrl ? await this.getScriptSrcFromManifest(manifestUrl, moduleFile) : moduleFile;

        if (!url) {
          return reject(`MmuRemoteModuleService: Could not resolve the script URL for module "${remoteModule}".`);
        }

        // Add the script to the DOM
        await this.loadScriptTag(url);

        // Initialize the webpack module container
        const moduleInstance = await this.initRemoteModule(remoteModule);
        this._logger.debug(`Module "${remoteModule}" successfully created.`);
        return resolve(moduleInstance);
      } catch (e) {
        // Throw a custom error to make it easier to debug and react on this error type
        reject(new RemoteModuleLoadingError(`Failed to load remote module "${remoteModule}". Reason:\n\n${e}`, e));
      }
    });

    this._moduleRegistry.set(remoteModule, promise);
    return promise;
  }

  /**
   * Initialize the default shared scope for webpack module federation. Only needs to be run once.
   *
   * @protected
   */
  protected async initWebpackShareScope(): Promise<void> {
    if (!this._isDefaultScopedInitialized) {
      this._logger.debug('Initializing webpack sharing scope "default".');
      await __webpack_init_sharing__('default');
      this._isDefaultScopedInitialized = true;
      this._logger.debug('Sharing scope "default" successfully initialized.');
    }
  }

  /**
   * Add the script tag with a given src to the DOM if not available yet.
   *
   * @param src The script source url
   * @protected
   */
  protected async loadScriptTag(src: string): Promise<void> {
    if (!this._scriptLoadCacheMap.has(src)) {
      const p = new Promise<void>((resolve, reject) => {
        const script = document.createElement('script') as HTMLScriptElement;
        script.onerror = (err: Event | string): void => reject(err);
        script.onload = (): void => resolve();
        script.src = src;
        document.body.appendChild(script);
        this._logger.debug('Added remote module script tag to DOM: ', script);
      });

      this._scriptLoadCacheMap.set(src, p);

      return p;
    } else {
      return this._scriptLoadCacheMap.get(src);
    }
  }

  /**
   * Initialize the remote webpack module dynamically. This has to be done after the script has been loaded in the DOM. Normally, webpack
   * can handle this itself, but not when the script is loaded dynamically into the DOM at runtime.
   *
   * @param module The webpack remote module, separated by a slash (e.g. 'scope/module')"
   * @protected
   */
  protected async initRemoteModule(module: string): Promise<RemoteModule> {
    let container: Container | undefined;
    const scope: string = module.split('/')[0];
    const moduleName: string = './' + module.split('/')[1];

    try {
      // Get the module container
      if (this._containerRegistry.has(scope)) {
        container = this._containerRegistry.get(scope);
        this._logger.debug(`Re-using existing container for scope "${scope}".`);
      } else {
        this._logger.debug(`Initializing container for scope "${scope}"...`);

        // We need to ignore the error, because the typescript compiler does not know that `scope` is of type string and
        // the global window object does not have a property with the name of `scope` (it's a variable).
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        container = window[scope] as Container;

        if (!container) {
          return Promise.reject(`MmuRemoteModuleService: Container for scope ${scope} is not available in the global window \
object. Has the remote script file been loaded already?`);
        }

        this._containerRegistry.set(scope, container);
        container.init(__webpack_share_scopes__.default);
        this._logger.debug(`Successfully initialized container "${scope}".`);
      }

      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const moduleFactory = await container!.get(moduleName);
      return Promise.resolve(moduleFactory());
    } catch (e) {
      return Promise.reject(e);
    }
  }

  /**
   * Load the manifest from the remote server and locate the "real" hashed script url inside the manifest.
   *
   * @param manifestUrl Where to load the manifest
   * @param file The (un-hashed) filename
   * @protected
   */
  protected async getScriptSrcFromManifest(manifestUrl: string, file: string): Promise<string | null> {
    if (!this._manifestRequestCacheMap.has(manifestUrl)) {
      const req = this._http.get(manifestUrl).pipe(shareReplay({ bufferSize: 1, refCount: false }));
      this._manifestRequestCacheMap.set(manifestUrl, req);
    }

    // Try to load manifest and get the real, hashed URL
    try {
      const request$: Observable<any> | undefined = this._manifestRequestCacheMap.get(manifestUrl);

      if (!request$) {
        return Promise.reject(new Error(`Manifest request for ${manifestUrl} was not found!`));
      }

      const manifest: Record<string, string> = await firstValueFrom(request$);

      if (!manifest[file]) {
        return Promise.reject(new Error(`Manifest did not contain information for file ${file}!`));
      }

      return manifest[file];
    } catch (e) {
      return Promise.reject(e);
    }
  }
}
